import type { DefaultRateKey, PayRateRole } from '../../../api/staff-pay-rates';
import { formatInstantInVenueZone, formatVenueDate } from '../../../utils/venueTime';

/** The order roles always appear in, on the roster and on the staff page. */
export const PAY_RATE_ROLES: PayRateRole[] = [
  'teacher',
  'assistant',
  'teacher_in_training',
  'travel_pay',
];

export const PAY_RATE_ROLE_LABELS: Record<PayRateRole, string> = {
  teacher: 'Teacher',
  assistant: 'Assistant',
  teacher_in_training: 'In Training',
  travel_pay: 'Travel',
};

const DEFAULT_KEY_MEANINGS: Record<DefaultRateKey, string> = {
  teacher: 'the company teacher rate',
  assistant: 'the company assistant rate',
  teacher_in_training: 'the company in-training rate',
  teacher_on_assistant_shift: 'the teacher on assistant shift rate',
};

export function describeDefaultKey(key: DefaultRateKey | null): string {
  return key ? DEFAULT_KEY_MEANINGS[key] : 'the company rate';
}

/** Cents as dollars, dropping ".00": 30000 → "$300", 12550 → "$125.50". */
export function formatRateCents(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** "250" / "$250.50" → cents, or null when it is not an amount. */
export function dollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * 100);
}

/** A YYYY-MM-DD calendar date, read as typed in whatever zone the viewer is in. */
export function formatCalendarDate(ymd: string): string {
  return formatVenueDate(ymd, 'MMM d, yyyy');
}

/** A YYYY-MM-DD calendar date without the year: "Nov 1". */
export function formatShortDate(ymd: string): string {
  return formatVenueDate(ymd, 'MMM d');
}

/** The most a per-shift rate can be: $1,000. */
export const MAX_RATE_CENTS = 100000;

const RECORDED_IN_ZONE = 'America/New_York';

/** When a rate was recorded, in the company's time zone. */
export function formatRecordedAt(iso: string): string {
  return formatInstantInVenueZone(iso, RECORDED_IN_ZONE);
}
